import type { PressureTrend } from '@/lib/nws/client'

// A 270° radial dial (gap at bottom): start 135°, span 270°. The needle sweeps from the
// low end to the reading on mount, then idles with a tiny oscillation — both CSS-driven
// (see chips.css) and disabled under prefers-reduced-motion.
const START = 135
const SPAN = 270
const LOW = 29.0
const HIGH = 30.7
const TICKS = 49
const CX = 50
const CY = 50

const pol = (r: number, deg: number): [number, number] => {
  const a = (deg * Math.PI) / 180
  return [CX + r * Math.cos(a), CY + r * Math.sin(a)]
}

export function Barometer({
  pressure,
  trend,
  size = 50,
}: {
  pressure: number | null
  trend: PressureTrend
  size?: number
}) {
  const frac = pressure == null ? 0.5 : Math.max(0, Math.min(1, (pressure - LOW) / (HIGH - LOW)))
  const angle = START + frac * SPAN

  const ticks = Array.from({ length: TICKS }, (_, i) => {
    const major = i % 6 === 0
    const a = START + (i / (TICKS - 1)) * SPAN
    const [x1, y1] = pol(major ? 35 : 38, a)
    const [x2, y2] = pol(42, a)
    return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} className={major ? 'baro-tick baro-tick--major' : 'baro-tick'} />
  })

  return (
    <svg className="baro" width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
      {ticks}
      {/* needle: outer group sweeps to the reading (keyed to replay on refresh), inner idles */}
      <g
        key={pressure ?? 'none'}
        className="baro-needle"
        style={{ '--ang': `${angle}deg` } as React.CSSProperties}
      >
        <g className="baro-idle">
          <line x1={CX} y1={CY} x2={CX} y2={CY - 32} className="baro-needle__line" />
        </g>
      </g>
      <circle cx={CX} cy={CY} r="4.5" className="baro-hub" />
      {/* trend chevron in the centre — points/streams down for falling, up for rising */}
      {trend !== 'steady' && (
        <g className={`baro-trend baro-trend--${trend}`}>
          <path d={trend === 'rising' ? 'M44 66 L50 59 L56 66' : 'M44 59 L50 66 L56 59'} />
        </g>
      )}
    </svg>
  )
}
